/**
 * Строки интерфейса. Английский — базовый язык; следующий (es) — такой же объект типа
 * Messages, и TypeScript не даст забыть ни одной строки.
 *
 * Ключи — «экран.смысл». Плейсхолдеры — {name}. Множественное число — пара ключей
 * .one/.other (выбор через Intl.PluralRules, у испанского появится .many).
 * Тексты героя (имя, слоган, описание, названия серий) приходят с сервера и сюда не входят.
 */
export const en = {
  // Общий сайт платформы: карточки опубликованных героев приходят из публичного каталога.
  "platform.brand": "Scrile AI",
  "platform.skip": "Skip to content",
  "platform.nav": "Main navigation",
  "platform.studio": "Creator studio",
  "platform.creators.nav": "For creators",
  "platform.partners.nav": "Partners",
  "platform.partners.eyebrow": "Partner program",
  "platform.partners.title.before": "Share a story.",
  "platform.partners.title.emphasis": "Earn when it grows.",
  "platform.partners.intro": "Recommend Scrile AI characters to your audience with a personal link or code. We keep the attribution and prepare partner earnings for payout.",
  "platform.partners.cta": "Apply as a partner",
  "platform.partners.card.label": "Example partner journey",
  "platform.partners.card.visit": "A listener visits. ",
  "platform.partners.card.visitBody": "Your code is remembered on the character page.",
  "platform.partners.card.join": "They subscribe. ",
  "platform.partners.card.joinBody": "The first valid source is attached to the purchase.",
  "platform.partners.card.earn": "You earn. ",
  "platform.partners.card.earnBody": "Eligible revenue is recorded for the agreed payout period.",
  "platform.partners.details.label": "Built into the platform",
  "platform.partners.details.title": "A simple link, with the accounting behind it.",
  "platform.partners.links.title": "Personal links and codes",
  "platform.partners.links.body": "Share a direct character URL with your referral code. Codes also work when a listener types one during signup or checkout.",
  "platform.partners.attribution.title": "Durable attribution",
  "platform.partners.attribution.body": "Scrile AI records the visit, carries it into the account and links eligible payments to the partner source.",
  "platform.partners.payouts.title": "Tracked earnings",
  "platform.partners.payouts.body": "Partner shares are separated in the platform ledger and prepared for payout after the program terms are agreed.",
  "platform.partners.final.label": "Private onboarding",
  "platform.partners.final.title": "Start with the audience you already know.",
  "platform.partners.final.body": "The partner program is currently approved manually. Tell us where you publish and which characters fit your audience; we will create the partner code and confirm commercial terms.",
  "platform.eyebrow": "Fiction, 18+ · awake after dark",
  "platform.title": "Someone is waiting up for you.",
  "platform.title.before": "Someone is",
  "platform.title.emphasis": "waiting up",
  "platform.title.after": "for you.",
  "platform.intro": "Four characters. Four lives already in motion. Put your headphones in or answer the message — every story starts free.",
  "platform.explore": "See who's awake",
  "platform.freeNote": "Episode one is free · no card",
  "platform.awake": "Luca is awake",
  "platform.catalog.nav": "Characters",
  "platform.catalog.label": "Who is awake tonight",
  "platform.catalog.title": "Choose the kind of company you want.",
  "platform.catalog.body": "A quiet voice, a late message, a mystery shared after midnight. Every character has a written season behind the conversation.",
  "platform.catalog.chat": "Chat story",
  "platform.catalog.audio": "Audio series",
  "platform.catalog.openChat": "Start chatting",
  "platform.catalog.listen": "Start listening",
  "platform.catalog.loading": "Loading characters",
  "platform.catalog.unavailable": "The character gallery is taking a moment to load. If you have an address, you can still open it below.",
  "platform.catalog.empty": "New characters are getting ready. Have an invitation? Open their address below.",
  "platform.catalog.find": "Open a character address",
  "platform.catalog.filterLabel": "Filter characters",
  "platform.catalog.filter.all": "All",
  "platform.catalog.filter.audio": "Voice",
  "platform.catalog.filter.chat": "Text",
  "platform.catalog.awakeCount": "{count} awake",
  "platform.promises.label": "How the stories work",
  "platform.promises.memory.title": "They remember",
  "platform.promises.memory.body": "What you say stays in the relationship and returns when it matters.",
  "platform.promises.schedule.title": "The story has a pulse",
  "platform.promises.schedule.body": "New scenes arrive on a schedule, then continue exactly where you left them.",
  "platform.promises.formats.title": "Listen or reply",
  "platform.promises.formats.body": "Choose close-mic audio for headphones or a text story that answers back.",
  "platform.formats": "Two ways to feel part of the story.",
  "platform.chat.label": "Chat stories",
  "platform.chat.title": "A new message. A little closer.",
  "platform.chat.body": "Get to know a character through messages, photos and voice notes. Reply in your own words as the story unfolds.",
  "platform.audio.label": "Audio series",
  "platform.audio.title": "Put your headphones on. Be there.",
  "platform.audio.body": "Settle into an episode made for listening. Follow the transcript, save your place and come back when the next chapter arrives.",
  "platform.find.title": "Your hero has a place of their own.",
  "platform.find.body": "Have a hero's name from a creator or an invitation? Enter their address name to visit their page.",
  "platform.find.label": "Hero address name",
  "platform.find.hint": "Use the name before the first dot in your hero's web address.",
  "platform.find.open": "Visit hero",
  "platform.find.invalid": "Enter a hero's address name using letters, numbers or hyphens.",
  "platform.creator.label": "For creators",
  "platform.creator.title": "Write someone worth waiting up for.",
  "platform.creator.body": "Bring the character and the season. Studio gives them a voice, their own page, a release schedule and a conversation that stays inside your plot.",
  "platform.creator.cta": "See how Studio works",
  "platform.creators.eyebrow": "For writers, voice actors and storytellers",
  "platform.creators.title": "Write someone worth waiting up for.",
  "platform.creators.title.before": "Write someone",
  "platform.creators.title.emphasis": "worth waiting up for.",
  "platform.creators.intro": "You bring a character and a season. We give them a voice, a page of their own, a release schedule and a chat that never breaks your plot.",
  "platform.creators.cta": "Request an invite",
  "platform.creators.explore": "See how it works",
  "platform.creators.bible.label": "Story Bible preview",
  "platform.creators.bible.season": "Season · promise",
  "platform.creators.bible.episode": "Episode 03 · cliffhanger",
  "platform.creators.bible.scene": "Scene 03.2 · what the AI knows",
  "platform.creators.formats.label": "Your story, your format",
  "platform.creators.formats.title": "One family, two ways into the story.",
  "platform.creators.formats.body": "Voice and text characters use the same Story Bible, release engine, subscriptions and Studio. Their public pages adapt to the experience.",
  "platform.creators.chat.title": "Chat stories",
  "platform.creators.chat.body": "Plan scenes that unfold as messages, photos and voice notes. Readers answer in their own words while your character keeps the story moving.",
  "platform.creators.chat.cta": "See Theo's text page",
  "platform.creators.audio.title": "Audio series",
  "platform.creators.audio.body": "Publish voiced episodes with transcripts, scheduled releases and subscriber extras. Turn a season into a listening ritual.",
  "platform.creators.audio.cta": "See Luca's voice page",
  "platform.creators.steps.label": "From idea to audience",
  "platform.creators.steps.title": "From first scene to a living season.",
  "platform.creators.step1.title": "Shape the character",
  "platform.creators.step1.body": "Define their identity, visual world, voice and the boundaries that keep every interaction consistent.",
  "platform.creators.step2.title": "Build the season",
  "platform.creators.step2.body": "Write episodes or dialogue scenes, add media and decide what is free, scheduled or reserved for subscribers.",
  "platform.creators.step3.title": "Set the terms",
  "platform.creators.step3.body": "Choose free episodes, trial length, monthly, yearly or whole-season access, then set the release calendar.",
  "platform.creators.step4.title": "Publish and get paid",
  "platform.creators.step4.body": "Submit for moderation, go live on a dedicated address and follow subscribers, retention, revenue and payouts.",
  "platform.creators.business.label": "A creative business, not just a demo",
  "platform.creators.business.title": "Make the relationship sustainable.",
  "platform.creators.business.body": "Give people a reason to come back while you decide how each character earns.",
  "platform.creators.business.pricing.title": "Flexible offers. ",
  "platform.creators.business.pricing.body": "Set monthly and annual subscriptions, a trial, one-time season access and tips.",
  "platform.creators.business.audience.title": "One audience account. ",
  "platform.creators.business.audience.body": "Followers keep one Scrile AI login as they discover more of your characters.",
  "platform.creators.business.tools.title": "One place to operate. ",
  "platform.creators.business.tools.body": "Manage publishing, dialogue, moderation, subscriptions and performance in the creator studio.",
  "platform.creators.final.label": "Start with one character",
  "platform.creators.final.title": "The next world can begin today.",
  "platform.creators.final.body": "Open the studio, create the character and choose how your audience will meet them.",
  "platform.footer": "Creator-led stories. AI characters. For adults 18+.",
  "platform.missing": "This page isn't here. Start at Scrile AI or open your hero's own address.",
  "platform.home": "Back to Scrile AI",
  // Общее
  "app.loading": "Loading…",
  "app.retry": "Try again",
  "app.back": "Back",
  "app.close": "Close",
  "app.onScrile": "on Scrile",
  "app.fatal.title": "This page didn't load",
  "app.missing.title": "There's no one here yet",
  "app.missing.body": "This address doesn't belong to a hero on Scrile.",
  "app.notFound.title": "Page not found",
  "app.notFound.body": "The link may be old. The hero's page is one tap away.",
  "app.notFound.cta": "Go to {hero}",

  "nav.signIn": "Sign in",
  "nav.account": "Your account",
  "nav.home": "{hero} — home",
  "nav.listen": "Listen",

  // Страница героя
  "hero.format.audio": "Audio series",
  "hero.format.chat": "Chat story",
  "hero.startPilot": "Start the pilot — free, no card",
  "hero.continue": "Continue listening",
  "hero.continueEpisode": "Continue · Episode {n}",
  "hero.openChat": "Open the chat",
  "hero.startChat": "Start chatting — free, no card",
  "hero.continueChat": "Continue the chat",
  "hero.chatNote.one": "Episode 1 is free. A free account, so {hero} knows your name.",
  "hero.chatNote.other": "The first {count} episodes are free. A free account, so {hero} knows your name.",
  "hero.pilotNote.one": "Episode 1 is free. No card, no sign-up.",
  "hero.pilotNote.other": "The first {count} episodes are free. No card, no sign-up.",
  "hero.trialLink": "Or subscribe — {days} days free",
  "hero.subscribeLink": "Or subscribe",
  "hero.trailer": "Listen to the trailer",
  "hero.trailerPause": "Pause the trailer",
  "hero.episodes": "Episodes",
  "hero.season": "Season {n}",
  "hero.episodesCount.one": "{count} episode",
  "hero.episodesCount.other": "{count} episodes",
  "hero.episodesLoading": "Loading episodes…",
  "hero.weekly.title": "What you get every week",
  "hero.weekly.episode.title": "A new episode",
  "hero.weekly.episode.body": "8–15 minutes. The story picks up right where it stopped.",
  "hero.weekly.rubric.title": "A rubric",
  "hero.weekly.rubric.body": "No plot, just the mood of the place — for nights you want company, not a story.",
  "hero.weekly.voice.title": "A voice note with your name",
  "hero.weekly.voice.body": "Short and personal. {hero} says your name, out loud.",
  "hero.weekly.chat.messages.title": "Messages on their own time",
  "hero.weekly.chat.messages.body": "Scenes arrive through the week, like texts from someone real.",
  "hero.weekly.chat.voice.title": "Voice notes",
  "hero.weekly.chat.voice.body": "Hear {hero} — and answer back.",
  "hero.weekly.chat.memory.title": "Remembers you",
  "hero.weekly.chat.memory.body": "What you tell {hero} stays in the story.",
  "hero.feature.length": "8–15 min episodes",
  "hero.feature.transcript": "Transcripts",
  "hero.feature.sleep": "Sleep timer",
  "hero.feature.name": "Says your name",
  "hero.pricing.title": "{days} days free, then {perMonth}/mo",
  "hero.pricing.titleNoTrial": "{perMonth}/mo, billed yearly",
  "hero.pricing.body": "Billed {yearly} a year, or {monthly} month to month. Cancel anytime.",
  "hero.pricing.cta": "See plans",
  "hero.madeBy": "Made by {creator}",

  // Серии
  "episode.number": "Episode {n}",
  "episode.minutes": "{min} min",
  "episode.free": "Free",
  "episode.locked": "Subscribers",
  "episode.upcoming": "Coming soon",
  "episode.played": "Played",
  "episode.playing": "Playing",
  "episode.left": "{time} left",
  "episode.out": "Out {date}",
  "episode.scheduled": "Out {date}",

  // Пейвол
  "paywall.title": "Keep listening to {hero}",
  "paywall.titleChat": "Keep talking to {hero}",
  "paywall.backToChat": "Back to the chat",
  "paywall.titleEpisode": "Episode {n} is waiting",
  "paywall.subtitle": "{days} days free. Cancel anytime.",
  "paywall.subtitleNoTrial": "Cancel anytime.",
  "paywall.plans": "Choose a plan",
  "paywall.plan.yearly": "Yearly",
  "paywall.plan.monthly": "Monthly",
  "paywall.plan.all_at_once": "All at once",
  "paywall.perMonth": "{price}/mo",
  "paywall.billedYearly": "billed {price}/yr",
  "paywall.billedMonthly": "billed monthly",
  "paywall.oneTime": "one-time",
  "paywall.save": "Save {percent}%",
  "paywall.allAtOnceNote": "Unlock the rest of this season now; your subscription still needed for new seasons.",
  "paywall.demoDay.badge": "Demo · 24h",
  "paywall.demoDay.oneTime": "test payment",
  "paywall.demoDay.note": "Demo only: unlock full voice and text access for 24 hours. No real payment is taken.",
  "paywall.demoDay.cta": "Activate 24-hour demo · {price}",
  "paywall.demoDay.charge": "Simulated {price} test payment. Access expires after 24 hours.",
  "paywall.demoDay.fine": "Demo checkout on the Scrile AI staging site. No card is charged.",
  "paywall.cta.trial": "Start {days}-day free trial",
  "paywall.cta.subscribe": "Subscribe",
  "paywall.cta.all_at_once": "Unlock the season · {price}",
  "paywall.then.yearly": "{days} days free, then {price}/yr.",
  "paywall.then.monthly": "{days} days free, then {price}/mo.",
  "paywall.charge.yearly": "{price} billed yearly.",
  "paywall.charge.monthly": "{price} billed monthly.",
  "paywall.charge.all_at_once": "One payment of {price}. No renewal.",
  "paywall.reminder": "We'll email you a day before the trial ends.",
  "paywall.fine": "Secure checkout with Stripe. Cancel anytime in your account.",
  "paywall.accountNext": "Next: a free account, so the hero knows your name.",
  "paywall.redirecting": "Taking you to checkout…",
  "paywall.canceled": "Checkout was canceled. Pick a plan whenever you're ready.",
  "paywall.subscribed.title": "You're subscribed",
  "paywall.subscribed.body": "New episodes arrive every week. Can't wait? Unlock the rest of the season now.",
  "paywall.everything.title": "You have every episode",
  "paywall.everything.body": "This season is unlocked. New seasons come with your subscription.",
  "paywall.backToListening": "Back to listening",

  // Вход и регистрация
  "auth.register.title": "Create your account",
  "auth.register.subtitle": "One account for every hero on Scrile. No card needed.",
  "auth.login.title": "Welcome back",
  "auth.login.subtitle": "Sign in to keep listening to {hero}.",
  "auth.name": "Your name",
  "auth.nameHint": "The hero will call you by this name.",
  "auth.nameRule": "Letters only, up to three words — the hero says it out loud.",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.passwordHint": "At least 8 characters.",
  "auth.age": "I'm 18 or older",
  "auth.code": "Code",
  "auth.codeOptional": "optional",
  "auth.codeHint": "From a creator's or partner's link.",
  "auth.codeToggle": "Have a code?",
  "auth.timezone": "New episodes follow your local time · {tz}",
  "auth.terms": "By continuing, you agree to the {terms} and the {privacy}.",
  "auth.termsLink": "Terms",
  "auth.privacyLink": "Privacy Policy",
  "auth.register.submit": "Create account",
  "auth.login.submit": "Sign in",
  "auth.toLogin": "Already have an account?",
  "auth.toLoginLink": "Sign in",
  "auth.toRegister": "New here?",
  "auth.toRegisterLink": "Create an account",
  "auth.invalid.email": "Enter a valid email.",
  "auth.invalid.password": "Use at least 8 characters.",
  "auth.invalid.passwordEmpty": "Enter your password.",
  "auth.invalid.name": "Letters only — up to three words and 40 characters.",
  "auth.invalid.age": "You need to be 18 or older.",
  "auth.invalid.code": "Codes are up to 32 characters.",

  // Плеер
  "player.play": "Play",
  "player.pause": "Pause",
  "player.back15": "Back 15 seconds",
  "player.forward15": "Forward 15 seconds",
  "player.speed": "Playback speed: {rate}×",
  "player.seek": "Position",
  "player.position": "{current} of {total}",
  "player.loading": "Loading…",
  "player.trailer": "Trailer",
  "player.transcript": "Transcript",
  "player.transcriptShow": "Show transcript",
  "player.transcriptHide": "Hide transcript",
  "player.transcriptNone": "This one has no transcript.",
  "player.caughtUp.title": "You're all caught up",
  "player.caughtUp.body": "New episodes come out every week.",
  "player.caughtUp.next": "Episode {n} comes out {date}.",
  "player.pilotEnd.title": "That was the pilot",
  "player.pilotEnd.body": "Episode {n} is ready when you are.",
  "player.pilotEnd.cta": "Keep listening",
  "player.welcome.trialing": "Your {days}-day free trial has started. Enjoy the next episode.",
  "player.welcome.active": "You're subscribed. Enjoy the next episode.",
  "player.welcome.unlocked": "The whole season is unlocked. Listen at your own pace.",
  "player.welcome.pending": "Confirming your payment…",
  "player.welcome.late": "Payment is still being confirmed. Refresh in a minute if episodes stay locked.",
  "player.season": "Season",
  "player.extras": "Extras",
  "player.extra.rubric": "Rubric",
  "player.extra.voice_note": "Voice note",
  "player.extras.locked": "Subscribers get a rubric and a voice note with their name every week.",
  "player.extras.empty": "Rubrics and voice notes appear here as they come out.",
  "player.mentioned": "Mentioned in this episode",
  "player.heroCard.label": "Mentioned just now",
  "player.heroCard.visit": "Visit",
  "player.gift": "Gift the hero",
  "player.giftNote": "A thank-you to {creator}. Gifts don't unlock episodes.",

  // Таймер сна
  "sleep.title": "Sleep timer",
  "sleep.button": "Sleep",
  "sleep.off": "Off",
  "sleep.minutes": "{min} min",
  "sleep.endOfEpisode": "End of episode",
  "sleep.test": "1 min (test)",
  "sleep.fadeNote": "The sound fades out gently before it stops.",
  "sleep.endsAtEpisodeEnd": "Ends with episode",
  "sleep.ended": "Sleep timer ended. Sweet dreams.",

  // Подарок герою
  "gift.title": "A gift for {hero}",
  "gift.body": "Gifts go to {creator}, who makes {hero}. They don't unlock episodes — they just say thank you.",
  "gift.amount": "Amount",
  "gift.note": "Add a note",
  "gift.notePlaceholder": "Optional, up to 200 characters",
  "gift.send": "Send {amount}",
  "gift.thanks": "Thank you. {creator} will see your gift.",

  // Аккаунт
  "account.title": "Account",
  "account.profile": "Profile",
  "account.name": "Name",
  "account.email": "Email",
  "account.timezone": "Time zone",
  "account.subscription": "Subscription to {hero}",
  "account.status.trialing": "Free trial",
  "account.status.active": "Active",
  "account.status.past_due": "Payment problem",
  "account.status.canceled": "Canceled",
  "account.status.paused": "Paused",
  "account.plan.yearly": "Yearly plan",
  "account.plan.monthly": "Monthly plan",
  "account.trialEnds": "Trial ends {date}",
  "account.trialEndsReminder": "We'll email you the day before.",
  "account.renews": "Renews {date}",
  "account.ends": "Ends {date}",
  "account.pastDue": "Update your payment method to keep listening.",
  "account.none": "You're not subscribed to {hero} yet.",
  "account.seePlans": "See plans",
  "account.manage": "Manage subscription",
  "account.unlocked": "Season unlocked: every episode of this season is yours.",
  "account.demo.status": "Demo active",
  "account.demo.plan": "24-hour full access",
  "account.demo.ends": "Demo access ends {date}",
  "account.demo.noCharge": "This is a staging demo. No card was charged and there is no renewal.",
  "account.signOut": "Sign out",
  "account.portalMock": "Mock mode: with the real server, Stripe's subscription page opens here.",

  // Мессенджер
  "messenger.log": "Messages with {hero}",
  "messenger.today": "Today",
  "messenger.yesterday": "Yesterday",
  "messenger.you": "You",
  "messenger.episodeStarted": "Episode {n} started",
  "messenger.typing": "typing…",
  "messenger.recording": "recording a voice message…",
  "messenger.sendingPhoto": "sending a photo…",
  "messenger.sendingVideo": "sending a video…",
  "messenger.typingAria": "{hero} is typing",
  "messenger.placeholder": "Message {hero}",
  "messenger.inputLabel": "Your message to {hero}",
  "messenger.send": "Send",
  "messenger.record": "Record a voice message",
  "messenger.recordSend": "Send voice message",
  "messenger.recordCancel": "Cancel recording",
  "messenger.recordingNow": "Recording",
  "messenger.recordLimit": "Voice messages are up to a minute — this one was sent at the limit.",
  "messenger.voice.denied": "Allow the microphone to send voice messages.",
  "messenger.voice.unsupported": "This browser can't record voice messages.",
  "messenger.voice.failed": "Recording didn't work. Try again.",
  "messenger.voice.too_short": "That was too short to send.",
  "messenger.voice.too_long": "Voice messages are up to a minute.",
  "messenger.voice.too_big": "That voice message is too large to send.",
  "messenger.voice.bad_type": "This recording format isn't supported.",
  "messenger.voice.label": "Voice message",
  "messenger.voice.play": "Play voice message",
  "messenger.voice.pause": "Pause voice message",
  "messenger.voice.seek": "Voice message position",
  "messenger.voice.showTranscript": "Show transcript",
  "messenger.voice.hideTranscript": "Hide transcript",
  "messenger.voice.noAudio": "The audio is on its way — here's what {hero} said.",
  "messenger.voice.failedToLoad": "This voice message didn't load.",
  "messenger.photo": "Photo",
  "messenger.photo.open": "Open photo",
  "messenger.photo.missing": "This photo is no longer available.",
  "messenger.video.missing": "This video is no longer available.",
  "messenger.lightbox": "Photo from {hero}",
  "messenger.card.label": "{hero} shared a contact",
  "messenger.card.open": "Open",
  "messenger.reminder": "Reminder",
  "messenger.busy": "{hero} is busy until the next episode.",
  "messenger.extraReady": "New: {title}",
  "messenger.status.sending": "Sending…",
  "messenger.status.sent": "Sent",
  "messenger.status.failed": "Not sent",
  "messenger.status.subscribe": "Not sent — subscribe to keep talking",
  "messenger.retry": "Retry",
  "messenger.discard": "Delete",
  "messenger.slowDown": "Slow down a little",
  "messenger.newMessages.one": "{count} new message",
  "messenger.newMessages.other": "{count} new messages",
  "messenger.jumpToLatest": "Jump to latest messages",
  "messenger.charsLeft": "{count} left",
  "messenger.tooLong": "Messages are up to {max} characters.",
  "messenger.loading": "Loading messages…",
  "messenger.empty.title": "{hero} will write first",
  "messenger.empty.body": "The story starts in a moment. Messages arrive on their own time — like texts from someone real.",
  "messenger.reconnecting": "Reconnecting…",
  "messenger.signedOut": "Your session has ended. Sign in to keep talking.",
  "messenger.signIn": "Sign in",
  "messenger.paywall.title": "Episode {n} is waiting",
  "messenger.paywall.titleGeneric": "Keep talking to {hero}",
  "messenger.paywall.body": "The free episodes are over. Subscribe and {hero} picks up right where you stopped.",
  "messenger.paywall.line": "Subscribers only from here",
  "messenger.paywall.plans": "See all plans",
  "messenger.welcome.trialing": "Your {days}-day free trial has started. {hero} will write soon.",
  "messenger.welcome.active": "You're subscribed. {hero} will write soon.",
  "messenger.welcome.pending": "Confirming your payment…",
  "messenger.announce.text": "{hero}: {text}",
  "messenger.announce.voice": "{hero} sent a voice message",
  "messenger.announce.photo": "{hero} sent a photo",
  "messenger.announce.video": "{hero} sent a video",
  "messenger.announce.card": "{hero} shared a contact: {name}",
  "messenger.aside.format": "Chat story",
  "messenger.aside.subscribed": "You're subscribed",
  "messenger.aside.freeNote.one": "Episode 1 is free.",
  "messenger.aside.freeNote.other": "The first {count} episodes are free.",
  "messenger.aside.page": "About {hero}",

  // Ошибки
  "error.validation": "Something doesn't look right. Check the fields and try again.",
  "error.not_found": "We couldn't find that. It may have been removed.",
  "error.unauthorized": "Please sign in to continue.",
  "error.forbidden": "You don't have access to this.",
  "error.payment_required": "This one is for subscribers.",
  "error.conflict": "That's already done.",
  "error.rate_limited": "Too many tries. Wait a minute and try again.",
  "error.internal": "Something went wrong on our side. Try again in a moment.",
  "error.network": "No connection. Check your internet and try again.",
  "error.unavailable": "The service is unavailable right now. Try again in a minute.",
  "error.bad_response": "We got an answer we didn't expect. Try again later.",
  "error.unknown": "Something went wrong. Try again.",
  "error.login.unauthorized": "Wrong email or password.",
  "error.register.conflict": "There's already an account with this email. Sign in instead.",
  "error.checkout.conflict": "You already have this. Check your account.",
  "error.checkout.unauthorized": "Sign in first, then pick a plan.",
  "error.episode.payment_required": "This episode is for subscribers.",
  "error.episode.not_found": "This episode isn't out yet.",
  "error.tip.validation": "Pick an amount between $1 and $500.",
  "error.audio": "The audio didn't load. Try again.",
} as const;

export type MessageKey = keyof typeof en;

/** Форма каталога для других языков: те же ключи, любые строки. */
export type Messages = { readonly [K in MessageKey]: string };
